import { createContext, ReactNode, use, useCallback, useEffect, useId, useRef, useState } from 'react';
import { confirmable, ConfirmDialog, createConfirmation } from 'react-confirm';
import styled from 'styled-components';
import {
  CustomMusic,
  CustomMusicManifest,
  PracticeVideos,
  SongPracticeVideos,
  SongSlug,
} from '../../../common/src/model.ts';
import * as server_api from '../../../common/src/server_api.ts';
import TrashIcon from '../assets/trash.svg?react';
import { useCurrentOrg } from '../data/use_current_org.tsx';
import { recordRecentlyAddedSong, recordRecentlyDeletedSong, useCustomMusic } from '../data/use_custom_music.tsx';
import { useSongVisibility } from '../data/use_song_visibility.ts';
import { useUserAttributes } from '../data/use_user_attributes.ts';
import { alert, confirm } from './confirm.tsx';
import { ModalSpinner } from './modal_spinner.tsx';
import { getHymnFromSlug, isCustomSong } from './path.ts';
import { isSamePracticeVideos, PracticeVideosSection, usePracticeVideoFields } from './practice_videos_section.tsx';
import { Pencil } from './shared.tsx';
import {
  Card,
  Dialog,
  DialogCloseButton,
  DoneButton,
  FieldLabel,
  Header,
  HideInHymnalRow,
  isValidPsalmNumber,
  NameAndPsalm,
  NameField,
  parsePsalmNumberField,
  PsalmField,
  TextField,
  Title,
  UploadProgress,
  useCustomSongUpload,
} from './song_dialog.tsx';
import { useHoldAppReload } from './use_hold_app_reload.ts';

const EditButtonContext = createContext<{ hasPermission: boolean } | undefined>(undefined);

export function EditButtonContextProvider({ children }: { children: ReactNode }) {
  const { isInternalUser, isOrgAdmin } = useUserAttributes();
  return <EditButtonContext value={{ hasPermission: isInternalUser || isOrgAdmin }}>{children}</EditButtonContext>;
}

// The org's practice video map with this song's entry replaced. The whole map goes to the server, so the cached
// org stays complete (updateOrgInfo merges fields shallowly), and the server merges it song by song: a song with
// no video left is sent as {}, which is what removes its videos there (mergeOrgUpsert).
function withSongPracticeVideos(
  practiceVideos: SongPracticeVideos | undefined,
  songSlug: SongSlug,
  videos: PracticeVideos,
): SongPracticeVideos {
  return { ...practiceVideos, [songSlug]: videos };
}

const EditCustomSong: ConfirmDialog<
  { customMusic: CustomMusic; customMusicManifest: CustomMusicManifest | undefined; songSlug: SongSlug },
  { songSlug?: SongSlug }
> = ({ customMusic, customMusicManifest, songSlug, proceed, show }) => {
  const onClose = useCallback(() => proceed({}), [proceed]);
  return (
    <EditSongDialog
      customMusic={customMusic}
      customMusicManifest={customMusicManifest}
      songSlug={songSlug}
      show={show}
      onClose={onClose}
    />
  );
};

/**
 * The edit-song pop-up for a custom song (the pencil in Our Hymnal): name, psalm number, practice videos
 * (GitHub issue #4041), hide in Our Hymnal, replace the PDF, delete the song. `isInline` renders it in the page
 * flow rather than as a modal, for Storybook.
 */
export function EditSongDialog({
  customMusic,
  customMusicManifest,
  songSlug,
  show,
  onClose,
  isInline = false,
}: {
  customMusic: CustomMusic;
  customMusicManifest: CustomMusicManifest | undefined;
  songSlug: SongSlug;
  show: boolean;
  onClose: () => void;
  isInline?: boolean;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (isInline) {
      return;
    }
    if (show) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [show, dialogRef, isInline]);

  const songNameInputRef = useRef<HTMLInputElement>(null);
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const fileChooserInputRef = useRef<HTMLInputElement>(null);
  const psalmNumberInputRef = useRef<HTMLInputElement>(null);
  const hymn = getHymnFromSlug({ songSlug, customMusic });
  const [songName, setSongName] = useState(() => hymn?.title);
  const songNameIsValid = !songName?.includes('/');
  let hymnPsalmNumber: number | undefined;
  {
    const { number } = hymn?.psalm?.match(/(?<number>\d+)/)?.groups ?? {};
    hymnPsalmNumber = number ? Number(number) : undefined;
  }
  const [psalmNumber, setPsalmNumber] = useState<number | undefined>(() => hymnPsalmNumber);
  const [showSpinner, setShowSpinner] = useState(false);
  const psalmNumberIsValid = isValidPsalmNumber(psalmNumber);
  const [isHiddenSong, setIsHiddenSong] = useState(() => !!currentOrg?.hiddenSongs?.includes(songSlug));
  const { setSongVisibility } = useSongVisibility();

  // the practice video link fields; the videos they hold are saved on Done
  const { videoFields, setVideoField, editedVideos } = usePracticeVideoFields(currentOrg?.practiceVideos?.[songSlug]);
  const baseId = useId();
  const ids = {
    name: `${baseId}-name`,
    psalm: `${baseId}-psalm`,
    hide: `${baseId}-hide`,
  };

  // A reload would take this dialog's unsaved name and psalm number away, so the dialog holds the app reload
  // while it is open; the upload holds it while in flight (issue #3265)
  useHoldAppReload(show);
  const upload = useCustomSongUpload({
    customMusic: currentOrg && customMusic[currentOrg.id],
    currentOrg,
    songName,
    psalmNumber,
  });

  useEffect(() => {
    songNameInputRef.current?.focus();
  }, [songNameInputRef, setPsalmNumber, songName]);

  const onSongNameChanged = useCallback(() => {
    setSongName(songNameInputRef.current?.value ?? '');
  }, [songNameInputRef, setSongName]);

  const onToggleHidden = useCallback(() => {
    const newValue = !isHiddenSong;
    setIsHiddenSong(newValue);
    void setSongVisibility({ songSlug, isVisible: !newValue });
  }, [isHiddenSong, setSongVisibility, songSlug]);

  const onPsalmNumberChanged = useCallback(() => {
    setPsalmNumber(parsePsalmNumberField(psalmNumberInputRef.current?.value));
  }, [psalmNumberInputRef, setPsalmNumber]);

  // saves the edited videos to the org when they differ from what it has; independent of the name and psalm
  const saveVideos = useCallback(() => {
    if (!currentOrg || isSamePracticeVideos(currentOrg.practiceVideos?.[songSlug], editedVideos)) {
      return;
    }
    // updateOrgInfo writes the cached org before it syncs, so the dialog can close without waiting on the network
    void updateOrgInfo({
      id: currentOrg.id,
      practiceVideos: withSongPracticeVideos(currentOrg.practiceVideos, songSlug, editedVideos),
    });
  }, [currentOrg, editedVideos, songSlug, updateOrgInfo]);

  const onClickUpload = useCallback(() => {
    if (!songNameIsValid) {
      void alert({ confirmation: <span>Song name may not contain a slash (/)</span> });
      return;
    }
    const number = songName?.match(/^Psalm (?<number>\d+)/)?.groups?.number;
    if (number && !psalmNumber) {
      setPsalmNumber(Number(number));
    }
    fileChooserInputRef?.current?.click();
  }, [psalmNumber, setPsalmNumber, songName, songNameIsValid]);

  const onDelete = useCallback(() => {
    if (!currentOrg?.id) {
      return;
    }
    void (async () => {
      if (
        !(await confirm({
          confirmation: (
            <span>Press Confirm to delete {hymn?.title ? `'${hymn.title}'` : 'song'} from your hymnal</span>
          ),
          confirmButtonText: 'Confirm',
          cancelButtonText: 'Cancel',
        }))
      ) {
        return;
      }
      setShowSpinner(true);
      try {
        await setSongVisibility({ songSlug, isVisible: true });
        await server_api.deleteCustomSong(songSlug);
        await recordRecentlyDeletedSong(customMusicManifest, currentOrg.id, songSlug);
        // the server dropped the song's practice videos with it; drop them from the cached org too
        if (currentOrg.practiceVideos?.[songSlug]) {
          const { [songSlug]: _deleted, ...otherSongs } = currentOrg.practiceVideos;
          await updateOrgInfo({ id: currentOrg.id, practiceVideos: otherSongs });
        }
        onClose();
      } catch (e: any) {
        setShowSpinner(false);
        await alert({ confirmation: <span>Error: {e.message}</span> });
      } finally {
        setShowSpinner(false);
      }
    })();
  }, [currentOrg, customMusicManifest, hymn, onClose, setSongVisibility, songSlug, updateOrgInfo]);

  const { setFileToUpload } = upload;
  const onFileChosen = useCallback(() => {
    const file = fileChooserInputRef?.current?.files?.[0];
    if (!file || !currentOrg || !songName) {
      console.log('no file');
      return;
    }
    setFileToUpload(file);
  }, [currentOrg, setFileToUpload, songName]);

  const onFinish = useCallback(() => {
    if (!currentOrg || !songName || !songNameIsValid || !psalmNumberIsValid || !hymn) {
      if (!currentOrg) {
        console.error(`Missing org`);
        onClose();
      }
      if (!hymn) {
        console.error(`Missing hymn`);
        onClose();
      }
      if (!songName) {
        void alert({ confirmation: <span>Missing song name</span> });
      }
      if (!songNameIsValid) {
        void alert({ confirmation: <span>Song name may not contain a slash (/)</span> });
        return;
      }
      if (!psalmNumberIsValid) {
        void alert({ confirmation: <span>Invalid Psalm number</span> });
      }
      return;
    }
    saveVideos();
    const anythingChanged = songName !== hymn.title || psalmNumber !== hymnPsalmNumber;
    if (!anythingChanged) {
      onClose();
      return;
    }
    void (async () => {
      setShowSpinner(true);
      try {
        await server_api.renameCustomSong({ songSlug, songName, psalmNumber });
        await recordRecentlyAddedSong({
          customMusicManifest,
          org: currentOrg,
          songSlug,
          songName,
          psalmNumber,
        });
        onClose();
      } catch (e: any) {
        setShowSpinner(false);
        await alert({ confirmation: <span>Error: {e.message}</span> });
      } finally {
        setShowSpinner(false);
      }
    })();
  }, [
    currentOrg,
    songName,
    songNameIsValid,
    psalmNumberIsValid,
    hymn,
    saveVideos,
    psalmNumber,
    hymnPsalmNumber,
    onClose,
    songSlug,
    customMusicManifest,
  ]);

  const onClickDialog = useCallback(
    (e: any) => {
      const didClickBackdrop = e.currentTarget === e.target;
      if (didClickBackdrop) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Dialog key="dialog" ref={dialogRef} onClick={onClickDialog} open={isInline || undefined} $isInline={isInline}>
      {upload.isShowingProgress ? (
        <UploadProgress
          status={upload.status}
          errorMessage={upload.errorMessage}
          onCancel={onClose}
          onRetry={upload.retry}
          onFinish={onFinish}
        />
      ) : (
        <Card>
          <input type="file" style={{ display: 'none' }} ref={fileChooserInputRef} onChange={onFileChosen} />
          <Header>
            <Title>Edit song</Title>
            <DialogCloseButton onClick={onClose} />
          </Header>

          <NameAndPsalm>
            <NameField>
              <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
              <TextField
                id={ids.name}
                type="text"
                onChange={onSongNameChanged}
                ref={songNameInputRef}
                defaultValue={songName}
                $isInvalid={!songNameIsValid}
              />
            </NameField>
            <PsalmField>
              <FieldLabel htmlFor={ids.psalm}>Psalm</FieldLabel>
              <TextField
                id={ids.psalm}
                type="number"
                placeholder="none"
                title={psalmNumberIsValid ? undefined : 'Invalid psalm number'}
                onChange={onPsalmNumberChanged}
                defaultValue={psalmNumber}
                ref={psalmNumberInputRef}
                $isInvalid={!psalmNumberIsValid}
                $isNarrow={true}
              />
            </PsalmField>
          </NameAndPsalm>

          <PracticeVideosSection videoFields={videoFields} setVideoField={setVideoField} editedVideos={editedVideos} />

          <HideInHymnalRow id={ids.hide} isHidden={isHiddenSong} onToggle={onToggleHidden} />

          <DoneButton type="button" onClick={onFinish}>
            Done
          </DoneButton>

          <SongActions>
            <SongActionButton type="button" onClick={onClickUpload}>
              <UploadIcon />
              Replace PDF
            </SongActionButton>
            <SongActionDivider />
            <SongActionButton type="button" onClick={onDelete} $isDangerous={true}>
              <Trash aria-hidden="true" />
              Delete song
            </SongActionButton>
          </SongActions>
        </Card>
      )}
      {showSpinner && <ModalSpinner />}
    </Dialog>
  );
}

const UploadIcon = () => (
  <svg
    aria-hidden="true"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 16V4m0 0L7 9m5-5l5 5M4 20h16" />
  </svg>
);

const SongActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 22px;
  margin-top: -4px;
`;

const SongActionButton = styled.button<{ $isDangerous?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: ${({ $isDangerous }) => ($isDangerous ? 'var(--color-text-dangerous)' : 'var(--color-nightingale)')};
  font-family: Jost-Regular, Arial, sans-serif;
  font-size: 13px;
  cursor: pointer;
`;

// the asset fills its box more than the design's icon did, so it is drawn smaller to look the same size
const Trash = styled(TrashIcon)`
  width: 10px;
  height: 10px;
  margin: 1.5px;
  fill: currentColor;
`;

const SongActionDivider = styled.div`
  width: 1px;
  height: 14px;
  background: var(--color-field-border);
`;

const editCustomSong = createConfirmation(confirmable(EditCustomSong));

export function EditSongButton({ songSlug }: { songSlug: SongSlug }) {
  const { customMusic, customMusicManifest } = useCustomMusic();
  const { hasPermission } = use(EditButtonContext) ?? {};
  const onClick = useCallback(
    async (event: any) => {
      event.stopPropagation();
      await editCustomSong({ customMusic, customMusicManifest, songSlug });
    },
    [customMusic, customMusicManifest, songSlug],
  );
  if (!hasPermission || !isCustomSong(songSlug)) {
    return;
  }
  return <Pencil style={{ width: '27px', height: '27px' }} onClick={onClick} />;
}
